/**
 * Avatar RAG Engine
 *
 * Orchestrates the Expert RAG pipeline for avatar mode:
 *   Mic → AudioCaptureService → Deepgram STT → RAG Connector → conversation.echo
 *
 * The avatar session runs in echo mode (pipeline_mode: "echo"), so Tavus's
 * ASR / LLM / TTS are disabled. This engine provides the intelligence layer,
 * feeding transcribed user speech through our RAG pipeline and echoing the
 * response text back to the avatar for lip-synced speech.
 */

import { AudioCaptureService } from '@/services/voice/AudioCaptureService'
import { DeepgramSTTProvider } from '@/services/voice/DeepgramSTTProvider'
import {
  createSessionConnector,
  registerSessionConnector,
  releaseSessionConnector,
  type RagConnector,
} from '@/services/ragConnector'
import { apiClient } from '@/services/apiClient'
import { sanitizeForTTS } from '@/utils/ttsSanitize'
import { voiceDeliveryText, type SpeechMode } from '@/utils/voiceDelivery'
import { VoiceTrackSplitter } from '@/utils/voiceTrack'
import type { AvatarSession } from '@/connectors/avatar'
import type { ChatMessage } from '@/services/ragConnector'
import type { RAGSource } from '@/types/source'
import type { RagConfig } from '@/stores/personaStore'
import type { STTEvents } from '@/types/voice'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AvatarRAGCallbacks {
  onUserMessage: (text: string) => void
  onAssistantChunk: (chunk: string) => void
  onAssistantComplete: (fullText: string) => void
  onSources: (sources: RAGSource[]) => void
  onStateChange: (state: AvatarRAGState) => void
  onError: (error: Error) => void
  onAudioLevel?: (level: number) => void
}

export type AvatarRAGState =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'processing'
  | 'speaking'
  | 'error'

// ---------------------------------------------------------------------------
// Sentence Buffer
// ---------------------------------------------------------------------------

/**
 * Buffers streaming text and emits complete sentences for progressive echo.
 * This reduces perceived latency — the avatar starts speaking the first sentence
 * while the RAG pipeline is still generating subsequent sentences.
 */
class SentenceBuffer {
  private buffer = ''

  add(chunk: string): string[] {
    this.buffer += chunk
    const sentences: string[] = []

    // Split on sentence-ending punctuation followed by whitespace
    // Handles: "Hello. World" → ["Hello.", "World" stays in buffer]
    const pattern = /([.!?])\s+/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(this.buffer)) !== null) {
      const sentence = this.buffer.slice(lastIndex, match.index + 1).trim()
      if (sentence) sentences.push(sentence)
      lastIndex = match.index + match[0].length
    }

    if (lastIndex > 0) {
      this.buffer = this.buffer.slice(lastIndex)
    }

    return sentences
  }

  flush(): string | null {
    const remaining = this.buffer.trim()
    this.buffer = ''
    return remaining || null
  }

  clear(): void {
    this.buffer = ''
  }
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class AvatarRAGEngine {
  private audioCapture: AudioCaptureService | null = null
  private sttProvider: DeepgramSTTProvider | null = null
  private sentenceBuffer = new SentenceBuffer()
  private abortController: AbortController | null = null
  // This session's own connector, never the chat's shared one (SA-1399).
  private readonly rag: RagConnector = createSessionConnector()

  private _state: AvatarRAGState = 'idle'
  private conversationHistory: ChatMessage[] = []
  private partialTranscript = ''
  private deepgramToken: string | null = null
  private tokenRefreshInterval: ReturnType<typeof setInterval> | null = null
  private isProcessing = false

  constructor(
    private session: AvatarSession,
    private callbacks: AvatarRAGCallbacks,
    private ragConfig: RagConfig,
    private ragServerUrl: string,
    private ragApiKey: string,
    private speechMode: SpeechMode = 'verbatim',
  ) {}

  get state(): AvatarRAGState {
    return this._state
  }

  private setState(newState: AvatarRAGState) {
    this._state = newState
    this.callbacks.onStateChange(newState)
  }

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  async start(): Promise<void> {
    if (this._state !== 'idle') return

    this.setState('connecting')

    try {
      // 1. Fetch initial Deepgram token
      this.deepgramToken = await this.fetchDeepgramToken()

      // 2. Initialize audio capture (requests mic access)
      this.audioCapture = new AudioCaptureService()
      this.audioCapture.onAudioLevel = (level) => {
        this.callbacks.onAudioLevel?.(level)
      }
      await this.audioCapture.initialize()

      // 3. Connect Deepgram STT
      await this.connectSTT()

      // 4. Wire audio capture → STT
      this.audioCapture.onAudioChunk = (pcmData) => {
        this.sttProvider?.sendAudio(pcmData)
      }

      // 5. Start token refresh (tokens have 30s TTL)
      this.tokenRefreshInterval = setInterval(async () => {
        try {
          this.deepgramToken = await this.fetchDeepgramToken()
        } catch (err) {
          console.warn('[AvatarRAGEngine] Token refresh failed:', err)
        }
      }, 25000)

      // 6. Configure RAG connector
      this.rag.setBaseUrl(this.ragServerUrl)
      if (this.ragApiKey) this.rag.setApiKey(this.ragApiKey)
      registerSessionConnector(this.rag)

      this.setState('listening')
    } catch (error) {
      console.error('[AvatarRAGEngine] Failed to start:', error)
      this.setState('error')
      this.callbacks.onError(error instanceof Error ? error : new Error('Failed to start'))
      throw error
    }
  }

  async stop(): Promise<void> {
    // Cancel in-flight RAG request
    this.cancelRAG()
    releaseSessionConnector(this.rag)

    // Stop token refresh
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval)
      this.tokenRefreshInterval = null
    }

    // Disconnect STT
    if (this.sttProvider) {
      this.sttProvider.close()
      this.sttProvider = null
    }

    // Release mic
    if (this.audioCapture) {
      this.audioCapture.destroy()
      this.audioCapture = null
    }

    this.sentenceBuffer.clear()
    this.partialTranscript = ''
    this.isProcessing = false

    this.setState('idle')
  }

  /** Interrupt current processing and avatar speech */
  interrupt(): void {
    this.cancelRAG()
    this.sentenceBuffer.clear()
    this.isProcessing = false
    this.session.interrupt()

    if (this._state !== 'idle') {
      this.setState('listening')
    }
  }

  /** Provide initial conversation history (e.g., when resuming a conversation) */
  setConversationHistory(history: ChatMessage[]): void {
    this.conversationHistory = [...history]
  }

  // -----------------------------------------------------------------------
  // Deepgram STT
  // -----------------------------------------------------------------------

  private async connectSTT(): Promise<void> {
    if (!this.deepgramToken) throw new Error('No Deepgram token')

    const events: STTEvents = {
      onTranscript: (text, isFinal, speechFinal) => {
        if (!text) return

        if (isFinal) {
          this.partialTranscript += (this.partialTranscript ? ' ' : '') + text

          if (speechFinal && this.partialTranscript.trim()) {
            this.handleFinalTranscript(this.partialTranscript.trim())
            this.partialTranscript = ''
          }
        }
      },
      onUtteranceEnd: () => {
        // Deepgram detected end of utterance — process what we have
        if (this.partialTranscript.trim() && !this.isProcessing) {
          this.handleFinalTranscript(this.partialTranscript.trim())
          this.partialTranscript = ''
        }
      },
      onError: (error) => {
        console.error('[AvatarRAGEngine] STT error:', error)
        this.callbacks.onError(error)
      },
      onOpen: () => {
        console.log('[AvatarRAGEngine] Deepgram connected')
      },
      onClose: () => {
        console.log('[AvatarRAGEngine] Deepgram disconnected')
      },
    }

    this.sttProvider = new DeepgramSTTProvider(events)
    await this.sttProvider.connect({
      token: this.deepgramToken,
      model: 'nova-3',
      language: 'en',
      interimResults: true,
      endpointingMs: 300,
      utteranceEndMs: 1000,
      encoding: 'linear16',
      sampleRate: 16000,
    })
  }

  // -----------------------------------------------------------------------
  // RAG Processing
  // -----------------------------------------------------------------------

  private async handleFinalTranscript(text: string): Promise<void> {
    if (this.isProcessing) return
    this.isProcessing = true

    // Notify UI of user message
    this.callbacks.onUserMessage(text)
    this.setState('processing')

    // Build messages for RAG
    const rag = this.rag
    const addendum = voiceDeliveryText(this.speechMode)
    const systemPrompt = addendum
      ? `${this.ragConfig.systemPrompt}\n\n## Voice Delivery\n${addendum}`
      : this.ragConfig.systemPrompt
    const messages = rag.buildMessages(
      systemPrompt,
      this.conversationHistory
        .filter((m): m is ChatMessage & { role: 'user' | 'assistant' } =>
          m.role === 'user' || m.role === 'assistant'
        ),
      text
    )

    // Cancel any previous request
    this.cancelRAG()
    this.abortController = new AbortController()
    this.sentenceBuffer.clear()

    const splitter = new VoiceTrackSplitter()
    let _fullResponse = ''
    let displayContent = ''
    let firstSentenceSent = false

    try {
      await rag.streamChatCompletion(
        {
          messages,
          model: this.ragConfig.model || undefined,
          temperature: this.ragConfig.temperature,
          max_tokens: this.ragConfig.maxTokens,
          top_k: this.ragConfig.topK,
          score_threshold: this.ragConfig.scoreThreshold,
          stream: true,
        },
        {
          onSources: (sources) => {
            this.callbacks.onSources(sources)
          },
          onToken: (token) => {
            _fullResponse += token

            // Voice-track split (spec 2026-07-28-voice-track-display-speech-split):
            // the reply may open with a <voice>...</voice> spoken track. The
            // splitter routes voice-block text to speech only and the
            // remainder to the display only; non-compliant replies pass
            // through to both — byte-identical to the pre-feature pipeline.
            const { voice, display } = splitter.push(token)
            if (display) {
              displayContent += display
              this.callbacks.onAssistantChunk(display)
            }

            // Progressive echo: split streaming text into sentences
            // and send each to the avatar. TavusSession internally
            // queues sentences while speaking and batches them on
            // stopped_speaking events.
            if (voice) {
              const sentences = this.sentenceBuffer.add(voice)
              for (const sentence of sentences) {
                const spoken = sanitizeForTTS(sentence)
                if (spoken) {
                  this.session.echo(spoken)
                }
                if (!firstSentenceSent) {
                  this.setState('speaking')
                  firstSentenceSent = true
                }
              }
            }
          },
          onComplete: (_content) => {
            // Finalize the splitter first — flush its tail through the same
            // voice/display routing (may include a fallback flush so a
            // transcript message never persists empty; see VoiceTrackSplitter).
            const tail = splitter.finalize()
            if (tail.display) {
              displayContent += tail.display
              this.callbacks.onAssistantChunk(tail.display)
            }
            if (tail.voice) {
              this.sentenceBuffer.add(tail.voice)
            }

            // Flush any remaining text in the sentence buffer
            const remaining = this.sentenceBuffer.flush()
            if (remaining) {
              const spoken = sanitizeForTTS(remaining)
              if (spoken) {
                this.session.echo(spoken)
              }
              if (!firstSentenceSent) {
                this.setState('speaking')
              }
            }

            const displayClean = displayContent.replace(/<\/?voice>/g, '').trim()

            // Update conversation history
            this.conversationHistory.push(
              { role: 'user', content: text },
              { role: 'assistant', content: displayClean }
            )

            this.callbacks.onAssistantComplete(displayClean)
            this.isProcessing = false
            this.setState('listening')
          },
          onError: (error) => {
            // Finalize the splitter FIRST so any buffered voice/display
            // content isn't silently discarded on a mid-stream error — the
            // splitter's buffering (unlike the old token-per-token pipeline)
            // makes this mandatory on every terminal path, not just
            // onComplete. Mirrors onComplete's flush exactly; finalize() is
            // idempotent, so there's no double-flush risk if onComplete
            // somehow also fires for the same stream.
            const tail = splitter.finalize()
            if (tail.display) {
              displayContent += tail.display
              this.callbacks.onAssistantChunk(tail.display)
            }
            if (tail.voice) {
              this.sentenceBuffer.add(tail.voice)
            }

            // Flush any remaining text in the sentence buffer so a partial
            // spoken sentence isn't cut mid-air.
            const remaining = this.sentenceBuffer.flush()
            if (remaining) {
              const spoken = sanitizeForTTS(remaining)
              if (spoken) {
                this.session.echo(spoken)
              }
              if (!firstSentenceSent) {
                this.setState('speaking')
              }
            }

            // Keep the partial reply in the transcript rather than losing it.
            const displayClean = displayContent.replace(/<\/?voice>/g, '').trim()
            if (displayClean) {
              this.conversationHistory.push(
                { role: 'user', content: text },
                { role: 'assistant', content: displayClean }
              )
              this.callbacks.onAssistantComplete(displayClean)
            }

            console.error('[AvatarRAGEngine] RAG error:', error)
            this.callbacks.onError(error)
            this.isProcessing = false
            this.setState('listening')
          },
        },
        { graphId: this.ragConfig.graphId },
      )
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return
      console.error('[AvatarRAGEngine] RAG request failed:', error)
      this.callbacks.onError(error instanceof Error ? error : new Error('RAG request failed'))
      this.isProcessing = false
      this.setState('listening')
    }
  }

  private cancelRAG(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.rag.cancelStream()
    this.sentenceBuffer.clear()
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  private async fetchDeepgramToken(): Promise<string> {
    const response = await apiClient.fetch('/api/voice/deepgram-token', {
      method: 'POST',
      body: JSON.stringify({ ttl_secs: 30 }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        (errorData as { detail?: string }).detail ||
          `Failed to get Deepgram token (${response.status})`
      )
    }

    const data = await response.json()
    return (data as { token: string }).token
  }
}
